import { useEffect, useState } from "react";
import "./Upload.css";
import { Trash2, Upload } from "lucide-react";
import Loading from "../../../../components/Loading";
import Hamburger from "../../../../components/Hamburger";
import { GridcoTitle } from "../../components";
import { uploadGridcoNetScheduleFile } from "../../../../Rest_api/RestApi";

const AutomationFileTypes = [
  "Net Schedule ISGS",
  "OPGC-1 (IBTPS Unit -1 & 2)",
  "OPGC-2 (IBTPS Unit -3 & 4)",
  "Vedanta",
  "GMR",
  "JIPL",
  "JSWEUL",
  "NBVL",
  "Maa Durga",
  "Net Schedule OA REMC",
  "Net Schedule OA GNA",
  "State Hydro Generation",
  "RTM day before",
  "RTM day current",
  "DAM day current",
];

type UploadedFile = {
  type: string;
  date: string;
  file_url: string;
  file: File;
  id: number;
};

const hyperlinkColumns: { [key: string]: string } = { file_url: "file_url" };

const FileTypeBackendAlias: { [key: string]: string } = {
  "Net Schedule ISGS": "isgs",
  "OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
  "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
  Vedanta: "vedanta",
  GMR: "gmr",
  JIPL: "jipl",
  JSWEUL: "jsweul",
  NBVL: "nbvl",
  "Maa Durga": "maa-durga",
  "Net Schedule OA REMC": "oa-remc",
  "Net Schedule OA GNA": "oa-gna",
  "State Hydro Generation": "state-hydro",
  "RTM day before": "rtm-day-before",
  "RTM day current": "rtm-day-current",
  "DAM day current": "dam-day-current",
};

export default function GridcoNetSchedule() {
  const [fileTypeList, setFileTypeList] =
    useState<string[]>(AutomationFileTypes);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const columnNames = ["Type", "Date", "File Link", "Delete"];
  const columnKeys = ["type", "date", "file_url", "id"];

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      let updatedfileTypes = [...AutomationFileTypes];
      for (let index = 0; index < uploadedFiles.length; index++) {
        let fileType = uploadedFiles[index].type;

        if (updatedfileTypes.includes(fileType)) {
          updatedfileTypes = updatedfileTypes.filter(
            (type) => type !== fileType
          );
        }
      }
      setFileTypeList(updatedfileTypes);
    } else {
      setFileTypeList([...AutomationFileTypes]);
    }
  }, [uploadedFiles]);

  function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!selectedDate) {
      alert("Please select a date");
      event.target.value = "";
      return;
    }

    if (!selectedFileType) {
      alert("Please select a file type");
      event.target.value = "";
      return;
    }

    if (!file) {
      alert("Please select a file");
      return;
    }

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const validExtensions = [".xlsx", ".pdf"];

    if (!validExtensions.includes(fileExtension)) {
      alert(`Invalid file format. Please select only XLSX or PDF files.`);
      event.target.value = "";
      return;
    }

    const newFile = {
      type: selectedFileType,
      date: selectedDate,
      file_url: URL.createObjectURL(file),
      file: file,
      file_name: file.name,
      id: uploadedFiles.length + 1,
    };

    setUploadedFiles((prevFiles) => [newFile, ...prevFiles]);
    setSelectedFileType("");
    event.target.value = "";
  }

  const renderTableCell = (item: any, columnKey: string) => {
    if (columnKey === "id") {
      // If the column is meant to display a delete button
      return (
        <div className="flex justify-center">
          <button
            className="text-red-500"
            onClick={() => {
              setUploadedFiles((prevFiles) =>
                prevFiles.filter((file) => file.id !== item.id)
              );
            }}
          >
            <Trash2 size={18} />
          </button>
        </div>
      );
    }
    if (hyperlinkColumns[columnKey]) {
      // If the column is meant to display a hyperlink
      const url = item[hyperlinkColumns[columnKey]]; // Get the URL from the data
      return (
        <div className="">
          <a
            href={url}
            className="text-primary font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            View File
          </a>
        </div>
      );
    }

    // Default behavior for non-hyperlink cells
    return (
      <div className="text-[#8C8A96] text-[12px]">
        {typeof item[columnKey] === "number"
          ? item[columnKey] % 1 === 0
            ? item[columnKey]
            : item[columnKey].toFixed(2)
          : item[columnKey]}
      </div>
    );
  };

  async function handleGenerate() {
    if (uploadedFiles.length === 0) {
      alert("Please upload a file");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      // Build metadata and append files
      const metaData = uploadedFiles.map((file, idx) => {
        const field = `file_${idx}`;
        formData.append(field, file.file);
        return {
          field, // reference to the file field
          date: file.date,
          type: FileTypeBackendAlias[file.type] || file.type,
        };
      });

      // Attach metadata as a single JSON string
      formData.append("metaData", JSON.stringify(metaData));

      const res = await uploadGridcoNetScheduleFile(formData);

      setUploadedFiles([]);
      setSelectedFileType("");
      setSelectedDate("");

      // Check if response contains a file URL
      if (!res.automation_files || !res.automation_files[0]?.file_url) {
        alert("No link found in the response");
        return;
      }

      // Get the file URL from response
      const fileUrl = res.automation_files[0].file_url;

      // First open a new tab, then trigger the download
      const newTab = window.open("about:blank", "_blank");
      if (newTab) {
        // Once the new tab is ready, navigate to the file URL
        setTimeout(() => {
          newTab.location.href = fileUrl;
        }, 100);
        alert(
          "File generated successfully! The download will begin in the new tab."
        );
      } else {
        // If popup blocked, provide direct link
        alert(
          "File generated successfully! Your browser may have blocked the popup. Click OK to download directly."
        );
        window.location.href = fileUrl;
      }
    } catch (error) {
      console.error("Error generating file:", error);
      alert("Error generating file. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full h-full ">
      <div className="dashboard-header flex items-center justify-between px-4">
        <Hamburger />
        <h5 className="text-[18px] text-[#6F6D7C] ms-5 font-medium">
          Bid Automation
        </h5>
        <div className="flex-1 flex justify-center">
          <GridcoTitle />
        </div>
      </div>
      <div className="m-4" style={{ height: "unset!important" }}>
        {loading && <Loading />}
        <div
          id="raj-upload"
          className="w-full mb-4 flex gap-10 items-center bg-white p-3 rounded-lg"
        >
          <div className="flex gap-3 items-center">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
              1
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-[#C5C5CBD9] rounded-lg p-2 text-[#8C8A96]"
            />
          </div>

          <div className="flex gap-3 items-center">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
              2
            </div>

            <select
              className="border border-[#C5C5CBD9] rounded-lg p-2 max-w-[200px] text-[#8C8A96] "
              value={selectedFileType}
              onChange={(e) => setSelectedFileType(e.target.value)}
            >
              <option value="" disabled>
                Select File Type
              </option>
              {fileTypeList.map((fileType, index) => (
                <option key={index} value={fileType}>
                  {fileType}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 items-center">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
              3
            </div>

            <div className="">
              <label className="downloadButton cursor-pointer font-medium flex items-center gap-3 h-[38px]">
                Upload File <Upload size={20} />
                <input
                  required={true}
                  type="file"
                  accept=".xlsx, .pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleFileUpload(e);
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="table-container bg-white rounded-xl">
          {/* Table Header */}
          <div className="table-contentHeading w-full">
            {columnNames.map((columnName, index) => (
              <div key={index} className="column-heading w-full">
                {columnName}
              </div>
            ))}
          </div>

          {/* Table Rows */}
          <div className="elemDetailsSec">
            {uploadedFiles.length > 0 ? (
              uploadedFiles.map((item, index) => (
                <div className="tableDetails border-b" key={index}>
                  {columnKeys.map((columnKey, index) => (
                    <div key={index} className="table-cell w-full text-center">
                      {renderTableCell(item, columnKey)}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-2 m-4 flex items-center justify-end">
        <button
          onClick={handleGenerate}
          disabled={uploadedFiles.length === 0}
          className="p-2 px-4 disabled:cursor-not-allowed disabled:bg-[#c4c4c4] text-white rounded-lg bg-[#F1935C] hover:shadow-lg transition-all duration-300 ease-in-out"
        >
          Generate
        </button>
      </div>

      <div className="gna-footer-container">
        <div className="grid grid-cols-2 justify-between gap-5 ">
          <p className="gna-footer-text">
            Powered by <strong>GNA Energy</strong>
          </p>
          <strong className="gna-footer-text  text-end">Beta Version</strong>
        </div>
      </div>
    </div>
  );
}
